import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Loading from 'common/components/atoms/Loading';
import {
  INK, INK_2, INK_3, LINE, LINE_2, BG, RADIUS, TYPE
} from 'common/styles/listUI';

/**
 * Generic list table for the redesigned list UI (sc-35741). Provides the
 * sticky, sortable header row, scroll-based infinite loading, empty state and
 * loading spinner. Columns are declarative: each supplies a `render(item)`
 * that returns the cell content (DataTable wraps it in a styled <td>).
 *
 * The exported styled primitives (Td, Tr, Dot, NameLink, ConditionCell, Empty)
 * are the building blocks for bespoke cell renderers on consuming pages.
 *
 * Test-id hooks (rowTestId / sortHeaderTestIdPrefix / emptyTestId / rowAttrs)
 * let a page reproduce an exact DOM contract — e.g. the Assets cucumber suite.
 */

// Upper bound on how long the infinite-scroll in-flight guard may stay closed
// without the parent responding. Comfortably longer than any real page fetch.
const LOAD_MORE_GUARD_TIMEOUT_MS = 5000;

const ScrollArea = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  /* The 12px of breathing room above the table is a margin, not padding, and
     that distinction is load-bearing. A sticky <th> cannot rise above its own
     <table>, so top padding *inside* the scrollport pins the header 12px down
     and leaves a 12px band that rows scroll up through — and because Td sets no
     background, they show over the gap. Keeping the space outside the scrollport
     pins the header flush with the top edge. */
  margin-top: 12px;
  padding: 0 20px 40px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: white;
  border: 1px solid ${LINE};
  border-radius: ${RADIUS.table};
  /* Deliberately no overflow:hidden here. It makes this table the containing
     scroll context for the sticky <th> below, so the header stops pinning to
     ScrollArea and scrolls out of view entirely. The square cell corners it
     used to clip are rounded per-cell instead. */

  thead tr th:first-child {
    border-top-left-radius: ${RADIUS.table};
  }

  thead tr th:last-child {
    border-top-right-radius: ${RADIUS.table};
  }

  tbody tr:last-child td:first-child {
    border-bottom-left-radius: ${RADIUS.table};
  }

  tbody tr:last-child td:last-child {
    border-bottom-right-radius: ${RADIUS.table};
  }
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  /* Must outrank every positioned element a cell can contain, or that element
     scrolls *over* the pinned header instead of under it. The common atoms'
     Checkbox sets z-index: 2 on its Container, which is why Devices in edit
     mode showed row checkboxes floating on top of the header row. Plain cell
     text is unpositioned and passes under regardless. */
  z-index: 3;
  padding: 9px 14px;
  font-size: ${TYPE.HEADER_SIZE};
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${INK_3};
  background: ${BG};
  border-bottom: 1px solid ${LINE};
  text-align: ${props => (props.alignCenter ? 'center' : 'left')};
  white-space: nowrap;
  cursor: ${props => (props.sortable ? 'pointer' : 'default')};
  user-select: none;

  &:hover {
    color: ${props => (props.sortable ? INK : INK_3)};
  }
`;

const SortGlyph = styled.span`
  margin-left: 5px;
  font-size: 9px;
  color: ${props => (props.active ? INK : '#c7cabf')};
`;

const Tr = styled.tr`
  cursor: ${(props) => {
    if (props.disabled) return 'not-allowed';
    return props.clickable ? 'pointer' : 'default';
  }};
  transition: background 0.12s ease;
  ${props => props.selected && 'background: rgba(145, 184, 70, 0.08);'}

  &:hover td {
    background: ${props => (props.clickable && !props.disabled ? BG : 'transparent')};
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: ${TYPE.CELL_SIZE};
  color: ${INK_2};
  border-bottom: 1px solid ${LINE_2};
  vertical-align: middle;
  text-align: ${props => (props.alignCenter ? 'center' : 'left')};
  ${props => props.color && `color: ${props.color};`}

  ${Tr}:last-child & {
    border-bottom: none;
  }
`;

const NameLink = styled(Link)`
  font-size: ${TYPE.NAME_SIZE};
  font-weight: ${TYPE.NAME_WEIGHT};
  color: ${INK};
  text-decoration: none;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: ${props => props.theme.primaryColor};
  }
`;

const Dot = styled.span`
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
  background: ${props => props.color || '#c9ccc4'};
`;

const ConditionCell = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${INK_2};
`;

const Empty = styled.div`
  padding: 48px 20px;
  text-align: center;
  color: ${INK_3};
  font-size: ${TYPE.CELL_SIZE};
`;

class DataTable extends React.Component {
  scrollRef = React.createRef();

  rowRefs = {};

  // Synchronous in-flight guard for infinite scroll. The `loading` prop is
  // flipped by the parent asynchronously (and on some pages behind a debounced
  // fetch), so a momentum/trackpad scroll burst can produce several scroll
  // events that all observe `loading === false` and each fire onLoadMore —
  // giving duplicated rows or a skipped page cursor. This flag closes in the
  // same tick as the call and reopens once the parent has actually responded.
  loadMorePending = false;

  loadMoreTimer = null;

  componentDidMount() {
    if (this.scrollRef.current) {
      this.scrollRef.current.addEventListener('scroll', this.handleScroll);
    }
  }

  componentDidUpdate(prevProps) {
    const { scrollToRowKey, rows, loading } = this.props;

    // Reopen the load-more guard once the parent has responded to the last
    // request: either new rows arrived, or the fetch settled (success or
    // failure, both of which drop `loading` back to false). Releasing on the
    // failure edge too means a failed page can never wedge infinite scroll.
    if (this.loadMorePending
      && (rows !== prevProps.rows || (prevProps.loading && !loading))) {
      this.releaseLoadMore();
    }

    if (scrollToRowKey != null && scrollToRowKey !== prevProps.scrollToRowKey) {
      this.scrollRowIntoView(scrollToRowKey);
    }
  }

  /**
   * Centres a row inside the table's own scroll viewport.
   *
   * Deliberately not `node.scrollIntoView()`: that walks every scrollable
   * ancestor, so selecting a device far down the Devices list also scrolled the
   * surrounding app frame and carried the nav bar off the top of the screen.
   * Driving our own scrollTop keeps the movement inside the table.
   *
   * Offsets come from getBoundingClientRect rather than offsetTop because the
   * rows' offsetParent is whatever positioned ancestor a page happens to supply
   * (ListContainer on Devices), which is not the scroll container.
   */
  scrollRowIntoView = (key) => {
    const node = this.rowRefs[key];
    const container = this.scrollRef.current;
    if (!node || !container || !node.getBoundingClientRect) return;

    const nodeRect = node.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const offsetWithin = nodeRect.top - containerRect.top;
    const centred = offsetWithin - ((container.clientHeight - nodeRect.height) / 2);
    const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
    const top = Math.max(0, Math.min(container.scrollTop + centred, maxScroll));

    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top, behavior: 'smooth' });
    } else {
      container.scrollTop = top;
    }
  };

  componentWillUnmount() {
    if (this.scrollRef.current) {
      this.scrollRef.current.removeEventListener('scroll', this.handleScroll);
    }
    clearTimeout(this.loadMoreTimer);
  }

  releaseLoadMore = () => {
    this.loadMorePending = false;
    clearTimeout(this.loadMoreTimer);
    this.loadMoreTimer = null;
  };

  handleScroll = () => {
    const el = this.scrollRef.current;
    if (!el) return;
    if ((el.scrollHeight - el.offsetHeight) - el.scrollTop < this.props.scrollThreshold) {
      const { hasMore, loading, onLoadMore } = this.props;
      if (hasMore && !loading && !this.loadMorePending) {
        this.loadMorePending = true;
        // Watchdog: some consumers' onLoadMore early-returns without
        // dispatching anything (e.g. their own thunk-level in-flight guard).
        // That produces no re-render, so componentDidUpdate would never run and
        // the guard above would wedge infinite scroll until remount. Capping
        // the guard's lifetime keeps a stuck consumer recoverable.
        clearTimeout(this.loadMoreTimer);
        this.loadMoreTimer = setTimeout(this.releaseLoadMore, LOAD_MORE_GUARD_TIMEOUT_MS);
        onLoadMore();
      }
    }
  };

  // Activate a sortable header from the keyboard the way a click would — on
  // Enter or Space only, so the header stays usable without a pointer.
  handleHeaderKeyDown = column => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.updateSort(column);
    }
  };

  isSortable = (column) => {
    const { onSortChange } = this.props;
    return Boolean(onSortChange) && !column.noSorter;
  };

  updateSort = (column) => {
    const { sort, onSortChange } = this.props;
    if (!this.isSortable(column)) return;

    if (!sort || sort.name !== column.name) {
      onSortChange({ name: column.name, order: 'asc' });
    } else {
      onSortChange({ name: column.name, order: sort.order === 'asc' ? 'desc' : 'asc' });
    }
  };

  sortGlyph = (column) => {
    const { sort } = this.props;
    if (!this.isSortable(column)) return null;
    const active = sort && sort.name === column.name;
    let glyph = '⇅';
    if (active) glyph = sort.order === 'asc' ? '▲' : '▼';
    return <SortGlyph active={active}>{glyph}</SortGlyph>;
  };

  getAriaSort = (column) => {
    const { sort } = this.props;
    if (!this.isSortable(column)) return undefined;
    if (!sort || sort.name !== column.name) return 'none';
    return sort.order === 'asc' ? 'ascending' : 'descending';
  };

  render() {
    const {
      columns, rows, getRowKey, onRowClick, isRowSelected, isRowDisabled,
      rowAttrs, rowTestId, sortHeaderTestIdPrefix, emptyTestId, loading,
      emptyMessage
    } = this.props;
    const clickable = Boolean(onRowClick);
    // defaultProps only fill in `undefined`, so an explicit `rows={null}` (easy
    // to pass from a page that has not fetched yet) would otherwise throw here.
    const safeRows = rows || [];

    return (
      <ScrollArea innerRef={(node) => { this.scrollRef.current = node; }}>
        {/* `table-items` is the hook the cucumber suite waits on to know a list
            has finished loading. It is applied only once loading is done,
            matching the legacy List component this table replaced — waiting on
            a marker that were present during load would let steps read an
            empty table. */}
        <Table className={loading ? undefined : 'table-items'}>
          <thead>
            <tr>
              {columns.map((column) => {
                const sortable = this.isSortable(column);
                return (
                <Th
                  key={column.name}
                  alignCenter={column.alignCenter}
                  sortable={sortable}
                  data-testid={sortHeaderTestIdPrefix ? `${sortHeaderTestIdPrefix}${column.name}` : undefined}
                  aria-sort={this.getAriaSort(column)}
                  // The <th> keeps its implicit columnheader role (which is what
                  // carries aria-sort); tabIndex + onKeyDown make the sort
                  // trigger reachable and operable without a pointer.
                  tabIndex={sortable ? 0 : undefined}
                  onKeyDown={sortable ? this.handleHeaderKeyDown(column) : undefined}
                  onClick={sortable ? () => this.updateSort(column) : undefined}
                >
                  {/* `th-label` is the hook the cucumber suite reads header
                      text from and clicks to sort, carried over from the legacy
                      ListHeaders component. The click bubbles to the <Th> sort
                      handler, so wrapping the label changes nothing visually. */}
                  <span className="th-label">{column.label}</span>
                  {this.sortGlyph(column)}
                </Th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {safeRows.map((item, rowIndex) => {
              const rowKey = getRowKey(item, rowIndex);
              const rowDisabled = isRowDisabled(item);
              return (
              <Tr
                key={rowKey}
                innerRef={(node) => { this.rowRefs[rowKey] = node; }}
                clickable={clickable}
                disabled={rowDisabled}
                selected={isRowSelected(item)}
                data-testid={rowTestId}
                onClick={clickable && !rowDisabled ? () => onRowClick(item) : undefined}
                {...rowAttrs(item)}
              >
                {columns.map(column => (
                  <Td
                    key={column.name}
                    alignCenter={column.alignCenter}
                    color={column.cellColor ? column.cellColor(item) : undefined}
                  >
                    {column.render(item, rowIndex)}
                  </Td>
                ))}
              </Tr>
              );
            })}
          </tbody>
        </Table>
        {!loading && safeRows.length === 0 && (
          <Empty data-testid={emptyTestId}>{emptyMessage}</Empty>
        )}
        {loading && <Loading />}
      </ScrollArea>
    );
  }
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.node,
    alignCenter: PropTypes.bool,
    noSorter: PropTypes.bool,
    render: PropTypes.func.isRequired,
    cellColor: PropTypes.func
  })).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  getRowKey: PropTypes.func,
  onRowClick: PropTypes.func,
  isRowSelected: PropTypes.func,
  isRowDisabled: PropTypes.func,
  scrollToRowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rowAttrs: PropTypes.func,
  rowTestId: PropTypes.string,
  sortHeaderTestIdPrefix: PropTypes.string,
  emptyTestId: PropTypes.string,
  sort: PropTypes.shape({ name: PropTypes.string, order: PropTypes.string }),
  onSortChange: PropTypes.func,
  loading: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  scrollThreshold: PropTypes.number,
  emptyMessage: PropTypes.string
};

DataTable.defaultProps = {
  rows: [],
  getRowKey: (item, index) => (item && item.id != null ? item.id : index),
  onRowClick: null,
  isRowSelected: () => false,
  isRowDisabled: () => false,
  scrollToRowKey: null,
  rowAttrs: () => ({}),
  rowTestId: undefined,
  sortHeaderTestIdPrefix: undefined,
  emptyTestId: undefined,
  sort: null,
  onSortChange: null,
  loading: false,
  hasMore: false,
  onLoadMore: () => {},
  scrollThreshold: 80,
  emptyMessage: 'No results found'
};

export {
  ScrollArea, Table, Th, Td, Tr, NameLink, Dot, ConditionCell, Empty
};
export default DataTable;
